import { ReactElement, ReactNode, useMemo } from 'react';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { ResourceUsageCard } from '../../../components/ResourceUsageCard/ResourceUsageCard';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import classes from './PartnerAdminDashboard.module.css';
import { PersonGroupIcon, EnvelopeClosedIcon, TasklistIcon, PhoneIcon, BriefcaseIcon, WalletIcon } from '@navikt/aksel-icons';
import { useGetPartnerDashboardQuery } from '../../../hooks/queries/useGetPartnerDashboardQuery';
import { formatNOK } from '../../../utils/currencyFormatters';

const PAGE_TITLE = 'Partner Dashboard';
const PAGE_DESCRIPTION = 'Oversikt over dine kunder og aktivitet';

// Type Definitions - moved to top for better discoverability
type DashboardData = {
  readonly totalCustomers: number;
  readonly activeCustomers: number;
  readonly licenses: {
    readonly total: number;
    readonly used: number;
  };
  readonly surveys: {
    readonly completed: number;
    readonly planned: number;
  };
  readonly sms: {
    readonly used: number;
    readonly quota: number;
  };
  readonly cashback: {
    readonly monthlyCashbackNOK: number;
    readonly yearlyCashbackNOK: number;
  };
};

type StatCardConfig = {
  readonly title: string;
  readonly icon: ReactNode;
  readonly value: number | string;
  readonly meta?: string;
};

type StatCardProps = {
  readonly config: StatCardConfig;
};

const StatCard = ({ config }: StatCardProps): ReactElement => (
  <ContainerCard>
    <ContainerCard.Title icon={config.icon}>{config.title}</ContainerCard.Title>
    <ContainerCard.Body className={classes.statBody}>
      <div className={classes.statValue}>{config.value}</div>
      {config.meta && <div className={classes.statMeta}>{config.meta}</div>}
    </ContainerCard.Body>
  </ContainerCard>
);

type StatCardsGridProps = {
  readonly cards: readonly StatCardConfig[];
};

const StatCardsGrid = ({ cards }: StatCardsGridProps): ReactElement => (
  <div className={classes.statsGrid}>
    {cards.map((card) => (
      <StatCard key={card.title} config={card} />
    ))}
  </div>
);

/**
 * Create stat card configurations from dashboard data
 */
const createStatCards = (data: DashboardData): readonly StatCardConfig[] => [
  {
    title: 'Antall kunder',
    icon: <BriefcaseIcon />,
    value: data.totalCustomers,
    meta: `${data.activeCustomers} aktive`,
  },
  {
    title: 'Fullførte undersøkelser',
    icon: <TasklistIcon />,
    value: data.surveys.completed,
  },
  {
    title: 'Planlagte undersøkelser',
    icon: <EnvelopeClosedIcon />,
    value: data.surveys.planned,
  },
];

// Transform API data to dashboard data format
const transformDashboardData = (apiData: ReturnType<typeof useGetPartnerDashboardQuery>['data']): DashboardData | null => {
  if (!apiData) return null;

  return {
    totalCustomers: apiData.stats.totalCustomers,
    activeCustomers: apiData.stats.activeCustomers,
    licenses: {
      total: apiData.licenses.total,
      used: apiData.licenses.used,
    },
    surveys: {
      completed: apiData.stats.completedSurveys,
      planned: apiData.stats.plannedSurveys,
    },
    sms: {
      used: apiData.sms.used,
      quota: apiData.sms.quota,
    },
    cashback: {
      monthlyCashbackNOK: apiData.cashback.monthlyCashbackNOK,
      yearlyCashbackNOK: apiData.cashback.yearlyCashbackNOK,
    },
  };
};

// Layout Components
type DashboardHeaderProps = {
  readonly title: string;
  readonly description: string;
};

const DashboardHeader = ({ title, description }: DashboardHeaderProps): ReactElement => (
  <header className={classes.header}>
    <Heading as="h1">{title}</Heading>
    <Paragraph size="lg">{description}</Paragraph>
  </header>
);

type DashboardLayoutProps = {
  readonly header: ReactElement;
  readonly children: ReactNode;
};

const DashboardLayout = ({ header, children }: DashboardLayoutProps): ReactElement => (
  <div className={classes.container}>
    {header}
    {children}
  </div>
);

type DashboardContentProps = {
  readonly data: DashboardData;
};

const DashboardContent = ({ data }: DashboardContentProps): ReactElement => {
  const statCards = useMemo(() => createStatCards(data), [data]);

  return (
    <div className={classes.content}>
      <StatCardsGrid cards={statCards} />

      <div className={classes.resourcesSection}>
        <ResourceUsageCard
          title="Lisenser"
          icon={<PersonGroupIcon />}
          resource={data.licenses}
        />
        <ResourceUsageCard
          title="SMS-bruk"
          icon={<PhoneIcon />}
          resource={{ used: data.sms.used, total: data.sms.quota }}
          labels={{
            total: 'Totalt kvote',
            available: 'Gjenstående',
          }}
        />
        <ContainerCard>
          <ContainerCard.Title icon={<WalletIcon />}>Cashback</ContainerCard.Title>
          <ContainerCard.Body>
            <div className={classes.cashbackContainer}>
              <div className={classes.cashbackItem}>
                <div className={classes.cashbackLabel}>Månedlig</div>
                <div className={classes.cashbackValue}>{formatNOK(data.cashback.monthlyCashbackNOK)}</div>
              </div>
              <div className={classes.cashbackItem}>
                <div className={classes.cashbackLabel}>Årlig</div>
                <div className={classes.cashbackValue}>{formatNOK(data.cashback.yearlyCashbackNOK)}</div>
              </div>
            </div>
          </ContainerCard.Body>
        </ContainerCard>
      </div>

      <ContainerCard>
        <ContainerCard.Title>Aktivitet over tid</ContainerCard.Title>
        <ContainerCard.Body>
          <div className={classes.trendsPlaceholder}>
            <Paragraph>
              Trendgrafer for bruk og aktivitet vil vises her. Dette vil inkludere historikk for
              lisensbruk, undersøkelser, og cashback-utvikling over tid.
            </Paragraph>
          </div>
        </ContainerCard.Body>
      </ContainerCard>
    </div>
  );
};

// Main Component
export const PartnerAdminDashboard = (): ReactElement => {
  const { data: apiData, isLoading, error } = useGetPartnerDashboardQuery();
  const header = <DashboardHeader title={PAGE_TITLE} description={PAGE_DESCRIPTION} />;

  if (isLoading) {
    return (
      <DashboardLayout header={header}>
        <GhostLoader />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout header={header}>
        <Alert variant="danger" size="md">
          <Paragraph>Kunne ikke laste dashboard data. Vennligst prøv igjen senere.</Paragraph>
        </Alert>
      </DashboardLayout>
    );
  }

  const data = transformDashboardData(apiData);

  if (!data) {
    return (
      <DashboardLayout header={header}>
        <Alert variant="warning" size="md">
          <Paragraph>Ingen data tilgjengelig.</Paragraph>
        </Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout header={header}>
      <DashboardContent data={data} />
    </DashboardLayout>
  );
};
